import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { socialDraftsApi, type CreateDraftInput } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";

// ─── List ─────────────────────────────────────────────────────────────────────

export function useSocialDrafts(
	proId: string | null,
	options?: { refetchInterval?: number | false },
) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.list(proId ?? ""),
		queryFn: async () => {
			const response = await socialDraftsApi.list(proId as string);
			return response.data ?? { drafts: [], pendingCount: 0 };
		},
		enabled: !!proId,
		staleTime: 10_000,
		refetchInterval: options?.refetchInterval,
	});
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useSocialDraft(
	proId: string | null,
	draftId: string | null,
	options?: { refetchInterval?: number | false },
) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.detail(proId ?? "", draftId ?? ""),
		queryFn: async () => {
			const response = await socialDraftsApi.get(
				proId as string,
				draftId as string,
			);
			return response.data ?? null;
		},
		enabled: !!proId && !!draftId,
		staleTime: 10_000,
		refetchInterval: options?.refetchInterval,
	});
}

// ─── Download URL ─────────────────────────────────────────────────────────────

export function useSocialDraftDownloadUrl(
	proId: string | null,
	draftId: string | null,
) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.download(proId ?? "", draftId ?? ""),
		queryFn: async () => {
			const response = await socialDraftsApi.download(
				proId as string,
				draftId as string,
			);
			return response.data?.url ?? null;
		},
		enabled: !!proId && !!draftId,
		// Signed URLs expire — don't cache for too long
		staleTime: 4 * 60 * 1000, // 4 minutes
		gcTime: 5 * 60 * 1000,
	});
}

// ─── Music Tracks ─────────────────────────────────────────────────────────────

export function useMusicTracks(proId: string | null) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.musicTracks(proId ?? ""),
		queryFn: async () => {
			const response = await socialDraftsApi.listMusicTracks(proId as string);
			return response.data?.tracks ?? [];
		},
		enabled: !!proId,
		staleTime: 60 * 60 * 1000, // 1 hour
	});
}

// ─── Templates ────────────────────────────────────────────────────────────────

export function useTemplates(proId: string | null) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.templates(proId ?? ""),
		queryFn: async () => {
			const response = await socialDraftsApi.listTemplates(proId as string);
			return response.data?.templates ?? [];
		},
		enabled: !!proId,
		staleTime: 60 * 60 * 1000, // 1 hour
	});
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateDraft(proId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateDraftInput) =>
			socialDraftsApi.create(proId, input),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.socialDrafts.list(proId),
			});
		},
	});
}

export function useRegenerateDraft(proId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (projectId: string) =>
			socialDraftsApi.create(proId, { projectId, triggerSource: "auto" }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.socialDrafts.list(proId),
			});
		},
	});
}

export function useUpdateDraftCaption(proId: string, draftId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (captionEn: string) =>
			socialDraftsApi.updateCaption(proId, draftId, captionEn),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.socialDrafts.detail(proId, draftId),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.socialDrafts.list(proId),
			});
		},
	});
}

// ─── Voiceover Script Preview ────────────────────────────────────────────────

/**
 * Fetch the auto-generated voiceover script preview for a project.
 *
 * Disabled by default — pass `enabled: true` from the caller when the pro
 * toggles voiceover ON or clicks [Regenerate]. Script is deterministic per
 * (project, pro) so 5-min staleTime is safe. No retries: if the endpoint
 * fails, the UI falls back to an empty textarea with a placeholder, and the
 * [Regenerate] button is always visible for manual retry.
 */
export function useVoiceoverPreviewScript(
	proId: string | null,
	projectId: string | null,
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: queryKeys.socialDrafts.voiceoverPreviewScript(
			proId ?? "",
			projectId ?? "",
		),
		queryFn: async () => {
			const response = await socialDraftsApi.getVoiceoverPreviewScript(
				proId as string,
				projectId as string,
			);
			return response.data?.script ?? "";
		},
		enabled: !!proId && !!projectId && (options?.enabled ?? false),
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
}
